import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import QMSLayout from "../components/QMSLayout";
import { API, useAuth } from "../App";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import {
  Search, ClipboardCheck, FileText, Plus, Target, Zap,
  FolderOpen, PieChart, Bell, ArrowRight, Loader2, X,
  Calendar, Flag, List, LayoutGrid, GripVertical,
  CheckCircle, Clock
} from "lucide-react";

const PRIORITY_CONFIG = {
  critical: { color: "#F87171", bg: "rgba(248,113,113,0.1)", label: "Critical" },
  high: { color: "#FB923C", bg: "rgba(251,146,60,0.1)", label: "High" },
  medium: { color: "#FBBF24", bg: "rgba(251,191,36,0.1)", label: "Medium" },
  low: { color: "#4ADE80", bg: "rgba(74,222,128,0.1)", label: "Low" },
};

const KANBAN_COLUMNS = [
  { id: "open", label: "Open", color: "#6B7280" },
  { id: "in-progress", label: "In Progress", color: "#C9A84C" },
  { id: "under-review", label: "Under Review", color: "#3B82F6" },
  { id: "completed", label: "Completed", color: "#10B981" },
];

// Draggable task card for Kanban
const DraggableTaskCard = ({ task, isUpdating }) => {
  const { t } = useTranslation(['modules', 'common']);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.task_id,
    data: { status: task.status },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging || isUpdating ? 0.5 : 1,
  };

  const pCfg = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-3 hover:border-[rgba(255,255,255,0.15)] transition-colors cursor-grab active:cursor-grabbing"
      data-testid={`kanban-card-${task.task_id}`}
      {...attributes}
      {...listeners}
    >
      {isUpdating && (
        <div className="text-[10px] text-[#C9A84C] mb-1 flex items-center gap-1">
          <Loader2 size={10} className="animate-spin" /> Updating...
        </div>
      )}
      <div className="flex items-start gap-2">
        <GripVertical size={14} className="text-[#8B95A5] mt-0.5 shrink-0 opacity-40" />
        <div className="min-w-0 flex-1">
          <h4 className="text-white text-sm font-medium leading-snug">{task.title}</h4>
          {task.description && (
            <p className="text-[#8B95A5] text-xs mt-1 line-clamp-2">{task.description}</p>
          )}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-[10px] px-1.5 py-0.5 rounded-full capitalize"
              style={{ color: pCfg.color, backgroundColor: pCfg.bg }}>
              {task.priority}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full capitalize bg-[rgba(255,255,255,0.04)] text-[#8B95A5]">
              {task.category}
            </span>
            {task.due_date && (
              <span className="text-[#8B95A5] text-[10px] flex items-center gap-0.5">
                <Calendar size={9} /> {task.due_date}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ClientPortalPage = () => {
  const { t } = useTranslation(['modules', 'common']);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: "", description: "", category: "task", priority: "medium", due_date: "" });
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [taskView, setTaskView] = useState("kanban"); // "kanban" | "list"
  const [updatingTaskId, setUpdatingTaskId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/client/portal-summary`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load portal data", err);
    } finally {
      setLoading(false);
    }
  };

  const createTask = async () => {
    if (!taskForm.title) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/client-tasks`, taskForm, { withCredentials: true });
      setShowTaskForm(false);
      setTaskForm({ title: "", description: "", category: "task", priority: "medium", due_date: "" });
      fetchData();
    } catch (e) { console.error(e); }
    setSubmitting(false);
  };

  const updateTaskStatus = useCallback(async (taskId, newStatus) => {
    setUpdatingTaskId(taskId);
    try {
      await axios.patch(`${API}/client-tasks/${taskId}/status`, { status: newStatus }, { withCredentials: true });
      await fetchData(); // Refetch for data accuracy
    } catch (e) {
      const status = e.response?.status;
      const msg = status === 403 ? "Permission denied" : status === 404 ? "Task not found" : "Failed to update task status";
      console.error(msg, e);
      alert(msg);
    }
    setUpdatingTaskId(null);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || !active) return;

    const taskId = active.id;
    const task = (data?.tasks?.items || []).find(t => t.task_id === taskId);
    if (!task) return;

    // Determine target column from drop target
    const overElement = over.id;
    let targetStatus = null;

    // Check if dropped on a column droppable
    for (const col of KANBAN_COLUMNS) {
      if (overElement === `col-${col.id}`) {
        targetStatus = col.id;
        break;
      }
    }

    // Check if dropped on another task — infer column from that task's status
    if (!targetStatus) {
      const overTask = (data?.tasks?.items || []).find(t => t.task_id === overElement);
      if (overTask) {
        targetStatus = overTask.status;
      }
    }

    if (!targetStatus || targetStatus === task.status) return;
    updateTaskStatus(taskId, targetStatus);
  }, [data, updateTaskStatus]);

  if (loading) {
    return (
      <QMSLayout title={t('modules:clientPortal.title')}>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="animate-spin text-[#36A9E1]" size={32} />
        </div>
      </QMSLayout>
    );
  }

  const tasks = data?.tasks || {};
  const tasksByStatus = tasks.by_status || {};
  const allItems = tasks.items || [];
  const totalTasks = allItems.length;
  const completedCount = allItems.filter(t => t.status === "completed").length;
  const completedPct = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;

  const toolCards = [
    { title: "Gap Assessment", desc: "AI-powered compliance gap analysis", icon: Search, path: "/gap-assessment", count: data?.gap_assessments?.length || 0, color: "#36A9E1" },
    { title: "QMS Toolkit", desc: "Generate compliance documents", icon: FileText, path: "/quality-agreement", count: data?.quality_agreements?.length || 0, color: "#E8A838" },
    { title: "Market Intel", desc: "Competitor & market analysis", icon: PieChart, path: "/competitor-analysis", count: data?.competitor_analyses?.length || 0, color: "#A78BFA" },
    { title: "Audit Matrices", desc: "283+ auditable items", icon: ClipboardCheck, path: "/audit-matrices", count: null, color: "#4ADE80" },
    { title: "Client Documents", desc: "Upload & manage documents", icon: FolderOpen, path: "/client-documents", count: data?.documents?.total || 0, color: "#38BDF8" },
    { title: "Messages", desc: "Internal communications", icon: Bell, path: "/messages", count: data?.unread_messages || 0, color: "#F472B6" },
  ];

  const getColumnTasks = (statusId) => allItems.filter(t => t.status === statusId);

  return (
    <QMSLayout title={t('modules:clientPortal.title')}>
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4" data-testid="portal-welcome">
        <div>
          <h2 className="font-['Syne'] text-2xl font-bold text-white">
            Welcome back, {user?.name?.split(" ")[0] || "Client"}
          </h2>
          <p className="text-[#8B95A5] text-sm mt-1">Your compliance command center</p>
        </div>
        <button onClick={() => setShowTaskForm(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#36A9E1] hover:bg-[#2d8fbf] text-white text-sm font-medium rounded-[3px] transition-colors"
          data-testid="add-task-btn">
          <Plus size={16} /> Add Task
        </button>
      </div>

      {/* Tab Nav */}
      <div className="flex gap-1 mb-6 bg-[#1A1D26] p-1 rounded-[3px] w-fit" data-testid="portal-tabs">
        {[
          { id: "overview", label: t('modules:clientPortal.tabs.overview') },
          { id: "tasks", label: `${t('modules:clientPortal.tabs.tasks')} (${totalTasks})` },
          { id: "tools", label: t('modules:clientPortal.tabs.tools') },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm rounded-[3px] transition-colors ${
              activeTab === tab.id ? "bg-[#36A9E1] text-white font-medium" : "text-[#8B95A5] hover:text-white"
            }`} data-testid={`tab-${tab.id}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Create Task Modal */}
      {showTaskForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] w-full max-w-lg" data-testid="task-form-modal">
            <div className="p-5 border-b border-[rgba(255,255,255,0.07)] flex items-center justify-between">
              <h3 className="font-['Syne'] font-bold text-white">{t('modules:clientPortal.newTask')}</h3>
              <button onClick={() => setShowTaskForm(false)} className="text-[#8B95A5] hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-[#CBD5E1] text-sm mb-1.5">Title</label>
                <input value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="Task title..." className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none"
                  data-testid="task-title-input" />
              </div>
              <div>
                <label className="block text-[#CBD5E1] text-sm mb-1.5">Description</label>
                <textarea value={taskForm.description} onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Details..." rows={2} className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('modules:clientPortal.formCategory')}</label>
                  <select value={taskForm.category} onChange={e => setTaskForm({ ...taskForm, category: e.target.value })}
                    className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none"
                    data-testid="task-category-select">
                    <option value="task">{t('modules:clientPortal.categoryTask')}</option>
                    <option value="milestone">{t('modules:clientPortal.categoryMilestone')}</option>
                    <option value="deliverable">{t('modules:clientPortal.categoryDeliverable')}</option>
                    <option value="quick-win">{t('modules:clientPortal.categoryQuickWin')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('modules:clientPortal.formPriority')}</label>
                  <select value={taskForm.priority} onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })}
                    className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none"
                    data-testid="task-priority-select">
                    <option value="low">{t('modules:clientPortal.priorityLow')}</option>
                    <option value="medium">{t('modules:clientPortal.priorityMedium')}</option>
                    <option value="high">{t('modules:clientPortal.priorityHigh')}</option>
                    <option value="critical">{t('modules:clientPortal.priorityCritical')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('modules:clientPortal.formDueDate')}</label>
                <input type="date" value={taskForm.due_date} onChange={e => setTaskForm({ ...taskForm, due_date: e.target.value })}
                  className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none" />
              </div>
            </div>
            <div className="p-5 border-t border-[rgba(255,255,255,0.07)] flex justify-end gap-3">
              <button onClick={() => setShowTaskForm(false)} className="px-4 py-2 text-[#8B95A5] text-sm">{t('common:actions.cancel')}</button>
              <button onClick={createTask} disabled={submitting || !taskForm.title}
                className="px-5 py-2.5 bg-[#36A9E1] disabled:opacity-50 text-white text-sm font-medium rounded-[3px]"
                data-testid="submit-task-btn">
                {submitting ? t('modules:clientPortal.creating') : t('modules:clientPortal.createTask')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="portal-kpis">
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target size={16} className="text-[#36A9E1]" />
                <span className="text-[#8B95A5] text-xs uppercase tracking-wider">Progress</span>
              </div>
              <div className="text-2xl font-bold text-white">{completedPct}%</div>
              <div className="w-full h-1.5 bg-[#2D313D] rounded-full mt-2">
                <div className="h-full rounded-full bg-[#36A9E1] transition-all" style={{ width: `${completedPct}%` }} />
              </div>
            </div>
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Search size={16} className="text-[#E8A838]" />
                <span className="text-[#8B95A5] text-xs uppercase tracking-wider">Assessments</span>
              </div>
              <div className="text-2xl font-bold text-white">{data?.gap_assessments?.length || 0}</div>
              <span className="text-[#8B95A5] text-xs">{data?.gap_assessments?.filter(a => a.status === "completed").length || 0} completed</span>
            </div>
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-4">
              <div className="flex items-center gap-2 mb-2">
                <FolderOpen size={16} className="text-[#38BDF8]" />
                <span className="text-[#8B95A5] text-xs uppercase tracking-wider">Documents</span>
              </div>
              <div className="text-2xl font-bold text-white">{data?.documents?.total || 0}</div>
              <span className="text-[#8B95A5] text-xs">{data?.documents?.processed || 0} AI-processed</span>
            </div>
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-4">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardCheck size={16} className="text-[#4ADE80]" />
                <span className="text-[#8B95A5] text-xs uppercase tracking-wider">Audits</span>
              </div>
              <div className="text-2xl font-bold text-white">{data?.audits?.total || 0}</div>
              <span className="text-[#8B95A5] text-xs">{data?.audits?.completed || 0} completed</span>
            </div>
          </div>

          {/* Two Column: Quick Access + Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tool Hub */}
            <div className="lg:col-span-2">
              <h3 className="font-['Syne'] font-semibold text-white mb-3 flex items-center gap-2">
                <Zap size={16} className="text-[#E8A838]" /> Quick Access
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="tool-hub">
                {toolCards.map(tool => (
                  <div key={tool.path} onClick={() => navigate(tool.path)}
                    className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-4 cursor-pointer hover:border-[rgba(255,255,255,0.15)] transition-all group"
                    data-testid={`tool-card-${tool.path.slice(1)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <tool.icon size={20} style={{ color: tool.color }} />
                      {tool.count !== null && tool.count > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ color: tool.color, backgroundColor: `${tool.color}15` }}>
                          {tool.count}
                        </span>
                      )}
                    </div>
                    <h4 className="text-white text-sm font-medium group-hover:text-[#36A9E1] transition-colors">{tool.title}</h4>
                    <p className="text-[#8B95A5] text-xs mt-0.5">{tool.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Wins & Messages */}
            <div className="space-y-4">
              <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px]" data-testid="quick-wins-panel">
                <div className="p-4 border-b border-[rgba(255,255,255,0.05)]">
                  <h3 className="font-['Syne'] font-semibold text-white text-sm flex items-center gap-2">
                    <Zap size={14} className="text-[#4ADE80]" /> Quick Wins
                  </h3>
                </div>
                <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
                  {(data?.quick_wins_from_assessments || []).length === 0 ? (
                    <p className="text-[#8B95A5] text-xs text-center py-4">Run a Gap Assessment to discover quick wins</p>
                  ) : (
                    data.quick_wins_from_assessments.map((qw, i) => (
                      <div key={i} className="bg-[#111318] rounded-[3px] p-2.5">
                        <p className="text-[#CBD5E1] text-xs leading-relaxed">{qw.action}</p>
                        <span className="text-[#8B95A5] text-[10px] mt-1 block">{qw.source}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
              {(data?.unread_messages || 0) > 0 && (
                <div onClick={() => navigate("/messages")}
                  className="bg-[rgba(244,114,182,0.05)] border border-[rgba(244,114,182,0.15)] rounded-[3px] p-4 cursor-pointer hover:bg-[rgba(244,114,182,0.08)] transition-colors"
                  data-testid="unread-messages-card">
                  <div className="flex items-center gap-3">
                    <Bell size={18} className="text-[#F472B6]" />
                    <div>
                      <span className="text-white text-sm font-medium">{data.unread_messages} unread message{data.unread_messages > 1 ? "s" : ""}</span>
                      <p className="text-[#8B95A5] text-xs">View your inbox</p>
                    </div>
                    <ArrowRight size={14} className="text-[#8B95A5] ml-auto" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recent Assessments */}
          {(data?.gap_assessments || []).length > 0 && (
            <div data-testid="recent-assessments">
              <h3 className="font-['Syne'] font-semibold text-white mb-3 flex items-center gap-2">
                <Search size={16} className="text-[#36A9E1]" /> Recent Gap Assessments
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.gap_assessments.slice(0, 6).map(ga => (
                  <div key={ga.assessment_id} onClick={() => navigate("/gap-assessment")}
                    className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-4 cursor-pointer hover:border-[#36A9E1]/30 transition-all"
                    data-testid={`assessment-card-${ga.assessment_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        ga.status === "completed" ? "bg-[rgba(74,222,128,0.1)] text-[#4ADE80]" :
                        ga.status === "generating" ? "bg-[rgba(251,191,36,0.1)] text-[#FBBF24]" :
                        "bg-[rgba(248,113,113,0.1)] text-[#F87171]"
                      }`}>{ga.status}</span>
                      {ga.summary?.overall_readiness_percent != null && (
                        <span className="text-sm font-bold text-white">{Math.round(ga.summary.overall_readiness_percent)}%</span>
                      )}
                    </div>
                    <h4 className="text-white text-sm font-medium truncate">{ga.title}</h4>
                    <p className="text-[#8B95A5] text-xs mt-0.5 truncate">{ga.standard_name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tasks Tab — with List / Kanban toggle */}
      {activeTab === "tasks" && (
        <div className="space-y-4">
          {/* View Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {KANBAN_COLUMNS.map(col => (
                <div key={col.id} className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: col.color }} />
                  <span className="text-[#8B95A5] text-xs">{col.label}</span>
                  <span className="text-xs font-medium" style={{ color: col.color }}>
                    {getColumnTasks(col.id).length}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-1 bg-[#1A1D26] p-1 rounded-[3px]" data-testid="view-toggle">
              <button onClick={() => setTaskView("kanban")}
                className={`p-1.5 rounded-[3px] transition-colors ${taskView === "kanban" ? "bg-[#36A9E1] text-white" : "text-[#8B95A5] hover:text-white"}`}
                title="Board view" data-testid="view-kanban-btn">
                <LayoutGrid size={14} />
              </button>
              <button onClick={() => setTaskView("list")}
                className={`p-1.5 rounded-[3px] transition-colors ${taskView === "list" ? "bg-[#36A9E1] text-white" : "text-[#8B95A5] hover:text-white"}`}
                title="List view" data-testid="view-list-btn">
                <List size={14} />
              </button>
            </div>
          </div>

          {/* Kanban Board */}
          {taskView === "kanban" && (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4" style={{ minHeight: "400px" }} data-testid="kanban-board">
                {KANBAN_COLUMNS.map(col => {
                  const columnTasks = getColumnTasks(col.id);
                  return (
                    <KanbanColumn key={col.id} col={col} tasks={columnTasks} updatingTaskId={updatingTaskId} />
                  );
                })}
              </div>
            </DndContext>
          )}

          {/* List View */}
          {taskView === "list" && (
            <div data-testid="task-list-view">
              {allItems.length === 0 ? (
                <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-8 text-center">
                  <Target size={32} className="mx-auto mb-2 text-[#8B95A5] opacity-50" />
                  <p className="text-[#8B95A5] text-sm">No tasks yet. Click "Add Task" to create your first task.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {allItems.map(item => {
                    const pCfg = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.medium;
                    const col = KANBAN_COLUMNS.find(c => c.id === item.status) || KANBAN_COLUMNS[0];
                    return (
                      <div key={item.task_id} className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-4 flex items-center justify-between"
                        data-testid={`task-item-${item.task_id}`}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: col.color }} />
                          <div className="min-w-0">
                            <span className={`text-sm font-medium ${item.status === "completed" ? "text-[#8B95A5] line-through" : "text-white"}`}>
                              {item.title}
                            </span>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full capitalize" style={{ color: pCfg.color, backgroundColor: pCfg.bg }}>
                                {item.priority}
                              </span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full capitalize" style={{ color: col.color, backgroundColor: `${col.color}15` }}>
                                {col.label}
                              </span>
                              {item.due_date && (
                                <span className="text-[#8B95A5] text-[10px] flex items-center gap-0.5">
                                  <Calendar size={9} /> {item.due_date}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {item.status === "open" && (
                            <button onClick={() => updateTaskStatus(item.task_id, "in-progress")}
                              className="text-xs text-[#C9A84C] hover:text-white px-2 py-1 bg-[rgba(201,168,76,0.08)] rounded-[3px]">
                              Start
                            </button>
                          )}
                          {item.status === "in-progress" && (
                            <button onClick={() => updateTaskStatus(item.task_id, "under-review")}
                              className="text-xs text-[#3B82F6] hover:text-white px-2 py-1 bg-[rgba(59,130,246,0.08)] rounded-[3px]">
                              Review
                            </button>
                          )}
                          {(item.status === "under-review" || item.status === "in-progress") && (
                            <button onClick={() => updateTaskStatus(item.task_id, "completed")}
                              className="text-xs text-[#10B981] hover:text-white px-2 py-1 bg-[rgba(16,185,129,0.08)] rounded-[3px]">
                              Complete
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tools Tab */}
      {activeTab === "tools" && (
        <div className="space-y-6">
          <p className="text-[#8B95A5] text-sm">Access the full CHARSTAN suite from one place.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tools-grid">
            {toolCards.map(tool => (
              <div key={tool.path} onClick={() => navigate(tool.path)}
                className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-5 cursor-pointer hover:border-[rgba(255,255,255,0.15)] transition-all group"
                data-testid={`tools-card-${tool.path.slice(1)}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-[3px] flex items-center justify-center" style={{ backgroundColor: `${tool.color}15` }}>
                    <tool.icon size={20} style={{ color: tool.color }} />
                  </div>
                  <div>
                    <h4 className="text-white text-sm font-medium group-hover:text-[#36A9E1] transition-colors">{tool.title}</h4>
                    <p className="text-[#8B95A5] text-xs">{tool.desc}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  {tool.count !== null ? (
                    <span className="text-[#8B95A5] text-xs">{tool.count} items</span>
                  ) : (
                    <span className="text-[#8B95A5] text-xs">Explore</span>
                  )}
                  <ArrowRight size={14} className="text-[#8B95A5] group-hover:text-[#36A9E1] transition-colors" />
                </div>
              </div>
            ))}
          </div>

          {/* Recent QMS Toolkit Documents */}
          {(data?.quality_agreements || []).length > 0 && (
            <div data-testid="recent-qa-docs">
              <h3 className="font-['Syne'] font-semibold text-white mb-3 flex items-center gap-2">
                <FileText size={16} className="text-[#E8A838]" /> Recent QMS Toolkit Documents
              </h3>
              <div className="space-y-2">
                {data.quality_agreements.slice(0, 5).map(qa => (
                  <div key={qa.agreement_id} onClick={() => navigate("/quality-agreement")}
                    className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-3 flex items-center justify-between cursor-pointer hover:border-[#E8A838]/30 transition-all">
                    <div>
                      <span className="text-white text-sm">{qa.client_name}</span>
                      <span className="text-[#8B95A5] text-xs ml-2">{(qa.standards || []).join(", ")}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      qa.status === "completed" ? "bg-[rgba(74,222,128,0.1)] text-[#4ADE80]" : "bg-[rgba(251,191,36,0.1)] text-[#FBBF24]"
                    }`}>{qa.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </QMSLayout>
  );
};

// Kanban column with droppable area
const KanbanColumn = ({ col, tasks, updatingTaskId }) => {
  const { setNodeRef } = useSortable({ id: `col-${col.id}`, data: { type: "column" } });

  return (
    <div ref={setNodeRef} className="bg-[#1A1D26] rounded-[3px] flex flex-col" data-testid={`kanban-col-${col.id}`}>
      {/* Column Header */}
      <div className="px-3 py-2.5 border-b border-[rgba(255,255,255,0.05)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: col.color }} />
          <span className="text-white text-sm font-medium">{col.label}</span>
        </div>
        <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ color: col.color, backgroundColor: `${col.color}15` }}>
          {tasks.length}
        </span>
      </div>
      {/* Column Cards */}
      <div className="flex-1 p-2 space-y-2 overflow-y-auto" style={{ maxHeight: "500px", minHeight: "100px" }}>
        {tasks.length === 0 ? (
          <p className="text-[#8B95A5] text-xs text-center py-6" data-testid={`kanban-empty-${col.id}`}>No tasks</p>
        ) : (
          tasks.map(item => (
            <DraggableTaskCard key={item.task_id} task={t} isUpdating={updatingTaskId === item.task_id} />
          ))
        )}
      </div>
    </div>
  );
};

export default ClientPortalPage;
